import React, { useState } from 'react';
import {
  Box, Typography, Paper, Button, Grid, Chip, CircularProgress,
  Alert, Divider, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Stack,
} from '@mui/material';
import { Psychology as BrainIcon } from '@mui/icons-material';
import { ArrowLeft, AlertTriangle, Phone, MapPin, Activity } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { usePregnancy, useVitals } from '../hooks/usePregnancies';
import { getEmergenciesByPregnancyId } from '../services/emergency';
import type { RiskCategory } from '../types';

/**
 * Pregnancy Details page - Detailed view of a specific pregnancy
 */
const PregnancyDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch pregnancy data
  const {
    data: pregnancy,
    isLoading: isLoadingPregnancy,
    isError: isErrorPregnancy,
    error: errorPregnancy,
  } = usePregnancy(id || '');

  // Fetch vitals data
  const {
    data: vitals = [],
    isLoading: isLoadingVitals,
  } = useVitals(id || '');

  // Fetch emergency history
  const {
    data: emergencies = [],
    isLoading: isLoadingEmergencies,
  } = useQuery({
    queryKey: ['emergencies', 'pregnancy', id],
    queryFn: () => getEmergenciesByPregnancyId(id || ''),
    enabled: !!id,
  });

  const [vitalsOpen, setVitalsOpen] = useState(false);
  const [emergencyOpen, setEmergencyOpen] = useState(false);
  const [vitalsForm, setVitalsForm] = useState({ bp_systolic: '', bp_diastolic: '', heart_rate: '', temperature: '', weight: '' });
  const [emergencyForm, setEmergencyForm] = useState({ event_type: 'HIGH_BP_EMERGENCY', severity_level: 'high', symptoms: '' });
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const handleMLRiskAssessment = async () => {
    if (!pregnancy || vitals.length === 0) {
      setActionError('Need pregnancy data and at least one vital record for ML assessment');
      return;
    }

    // Note: Direct ML API calls from browser are blocked by CORS
    // The ML model is automatically called when vitals are recorded via the backend
    setActionSuccess(
      `AI Risk Assessment: ${(pregnancy.risk_category || 'unknown').toUpperCase()} RISK | ` +
      `Risk Score: ${pregnancy.risk_score || 0}/100 | ` +
      `ML model automatically analyzes vitals when recorded.`
    );
  };

  const handleRecordVitals = async () => {
    if (!pregnancy) return;
    if (!vitalsForm.bp_systolic || !vitalsForm.bp_diastolic || !vitalsForm.heart_rate) {
      setActionError('BP Systolic, BP Diastolic and Heart Rate are required');
      return;
    }
    setActionLoading(true);
    setActionError(null);
    try {
      const { recordVitals } = await import('../services/pregnancy');
      await recordVitals({
        pregnancy_id: pregnancy.pregnancy_id,
        bp_systolic: parseInt(vitalsForm.bp_systolic),
        bp_diastolic: parseInt(vitalsForm.bp_diastolic),
        heart_rate: parseInt(vitalsForm.heart_rate),
        temperature: vitalsForm.temperature ? parseFloat(vitalsForm.temperature) : 0,
        weight: vitalsForm.weight ? parseFloat(vitalsForm.weight) : 0,
        recorded_by: 'officer',
      }, queryClient);
      setVitalsOpen(false);
      setVitalsForm({ bp_systolic: '', bp_diastolic: '', heart_rate: '', temperature: '', weight: '' });
      setActionSuccess('Vitals recorded successfully');
      // Refetch vitals after recording
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['vitals', pregnancy.pregnancy_id] });
      }, 500);
    } catch (e: any) {
      setActionError(e.message ?? 'Failed to record vitals');
    } finally {
      setActionLoading(false);
    }
  };

  const handleTriggerEmergency = async () => {
    if (!pregnancy) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const { triggerEmergency } = await import('../services/emergency');

      // Get user info for triggered_by field
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      const triggeredBy = user?.email || 'officer';

      const result = await triggerEmergency({
        pregnancy_id: pregnancy.pregnancy_id,
        event_type: emergencyForm.event_type,
        severity: emergencyForm.severity_level,
        latitude: pregnancy.latitude || 0,
        longitude: pregnancy.longitude || 0,
        symptoms: emergencyForm.symptoms || undefined,
        triggered_by: triggeredBy,
      });

      console.log('Emergency triggered successfully:', result);

      setEmergencyOpen(false);
      setActionSuccess('Emergency triggered successfully');

      // Invalidate emergency queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['emergencies'] });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
    } catch (e: any) {
      console.error('Failed to trigger emergency:', e);
      setActionError(e.message ?? 'Failed to trigger emergency');
    } finally {
      setActionLoading(false);
    }
  };

  // Get risk category color
  const getRiskColor = (risk: RiskCategory): 'success' | 'warning' | 'error' => {
    switch (risk) {
      case 'low':
        return 'success';
      case 'medium':
        return 'warning';
      case 'high':
      case 'critical':
        return 'error';
      default:
        return 'success';
    }
  };

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  };

  // Format vitals data for chart
  const vitalsChartData = vitals.map((vital) => ({
    date: new Date(vital.timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }),
    systolic: vital.bp_systolic,
    diastolic: vital.bp_diastolic,
    heartRate: vital.heart_rate,
    temperature: vital.temperature,
    weight: vital.weight || 0,
  }));

  // Calculate risk score trend (mock data for now - would come from API)
  const riskScoreTrend = vitals.map((vital, index) => ({
    date: new Date(vital.timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }),
    riskScore: pregnancy ? pregnancy.risk_score - (vitals.length - index) * 2 : 0,
  }));

  // Loading state
  if (isLoadingPregnancy) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state - check for undefined ID first
  if (!id || id === 'undefined') {
    return (
      <Box>
        <Button startIcon={<ArrowLeft size={16} />} onClick={() => navigate('/pregnancies')} sx={{ mb: 3 }}>
          Back to List
        </Button>
        <Alert severity="error">
          Invalid pregnancy ID. Please select a pregnancy from the list.
        </Alert>
      </Box>
    );
  }

  // Error state
  if (isErrorPregnancy || !pregnancy) {
    return (
      <Box>
        <Button startIcon={<ArrowLeft size={16} />} onClick={() => navigate('/pregnancies')} sx={{ mb: 3 }}>
          Back to List
        </Button>
        <Alert severity="error">
          Failed to load pregnancy details:{' '}
          {errorPregnancy instanceof Error ? errorPregnancy.message : `Pregnancy with ID '${id}' not found`}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button startIcon={<ArrowLeft size={16} />} onClick={() => navigate('/pregnancies')} sx={{ mr: 2 }}>
            Back to List
          </Button>
          <Box>
            <Typography variant="h4" fontWeight={600}>
              {pregnancy.patient_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pregnancy ID: {pregnancy.pregnancy_id}
            </Typography>
          </Box>
        </Box>
        <Chip
          label={`${(pregnancy.risk_category || 'unknown').toUpperCase()} RISK`}
          color={getRiskColor(pregnancy.risk_category)}
          size="medium"
          icon={<AlertTriangle size={16} />}
        />
      </Box>

      {/* Patient Information */}
      <Paper sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Patient Information
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Age
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {pregnancy.age || 'N/A'} years
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Blood Type
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {pregnancy.blood_type || 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <Phone size={16} style={{ marginRight: 8, color: '#9e9e9e' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Phone
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {pregnancy.phone || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <MapPin size={16} style={{ marginRight: 8, color: '#9e9e9e' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Location
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {pregnancy.village || 'N/A'}, {pregnancy.district || 'N/A'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Expected Delivery Date
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {formatDate(pregnancy.edd)}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Risk Score
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {pregnancy.risk_score || 0}/100
              </Typography>
            </Box>
          </Grid>
          {pregnancy.medical_history && (
            <Grid item xs={12}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Medical History
                </Typography>
                <Typography variant="body1">{pregnancy.medical_history}</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Vitals History Chart */}
      <Paper sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Vital Signs History
        </Typography>
        <Divider sx={{ mb: 3 }} />
        {isLoadingVitals ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : vitalsChartData.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No vital signs recorded yet
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={vitalsChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="systolic" stroke="#dc2626" name="BP Systolic" />
              <Line type="monotone" dataKey="diastolic" stroke="#f59e0b" name="BP Diastolic" />
              <Line type="monotone" dataKey="heartRate" stroke="#10b981" name="Heart Rate" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Paper>

      {/* Risk Score Trend */}
      <Paper sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Risk Score Trend
        </Typography>
        <Divider sx={{ mb: 3 }} />
        {riskScoreTrend.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No risk score history available
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={riskScoreTrend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis domain={[0, 100]} />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="riskScore"
                stroke="#dc2626"
                fill="#fecaca"
                name="Risk Score"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Paper>

      {/* Emergency History */}
      <Paper sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Emergency History
        </Typography>
        <Divider sx={{ mb: 3 }} />
        {isLoadingEmergencies ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : emergencies.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No emergency events recorded
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Event Type</TableCell>
                  <TableCell>Severity</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Response Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emergencies.map((emergency) => (
                  <TableRow key={emergency.event_id}>
                    <TableCell>{formatDate(emergency.trigger_timestamp)}</TableCell>
                    <TableCell>{emergency.event_type}</TableCell>
                    <TableCell>
                      <Chip
                        label={emergency.severity_level.toUpperCase()}
                        size="small"
                        color={
                          emergency.severity_level === 'critical'
                            ? 'error'
                            : emergency.severity_level === 'high'
                              ? 'warning'
                              : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={emergency.status.toUpperCase()} size="small" />
                    </TableCell>
                    <TableCell>
                      {emergency.response_time_seconds
                        ? `${Math.floor(emergency.response_time_seconds / 60)}m`
                        : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {actionSuccess && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setActionSuccess(null)}>{actionSuccess}</Alert>}
      {actionError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setActionError(null)}>{actionError}</Alert>}

      {/* ML Risk Assessment Card */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          border: '2px solid',
          borderColor: pregnancy.risk_category === 'critical' ? '#dc2626' :
            pregnancy.risk_category === 'high' ? '#f59e0b' :
              pregnancy.risk_category === 'medium' ? '#3b82f6' : '#10b981',
          borderRadius: 2,
          background: pregnancy.risk_category === 'critical' ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)' :
            pregnancy.risk_category === 'high' ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' :
              pregnancy.risk_category === 'medium' ? 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)' :
                'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <BrainIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
          <Box>
            <Typography variant="h6" fontWeight={700}>
              AI-Powered Risk Assessment
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Machine Learning Model Analysis
            </Typography>
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Risk Level
              </Typography>
              <Typography variant="h5" fontWeight={700} color={
                pregnancy.risk_category === 'critical' ? 'error.main' :
                  pregnancy.risk_category === 'high' ? 'warning.main' :
                    pregnancy.risk_category === 'medium' ? 'info.main' : 'success.main'
              }>
                {(pregnancy.risk_category || 'unknown').toUpperCase()}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Risk Score
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {pregnancy.risk_score || 0}/100
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Vitals Recorded
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {vitals.length}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Alert severity="info" sx={{ mt: 2 }}>
          ML model automatically analyzes risk when vitals are recorded. Last updated: {vitals.length > 0 ? formatDate(vitals[0].timestamp) : 'N/A'}
        </Alert>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button variant="outlined" startIcon={<Activity size={16} />} onClick={() => { setVitalsOpen(true); setActionError(null); }}>
          Record Vitals
        </Button>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<BrainIcon />}
          onClick={handleMLRiskAssessment}
          disabled={vitals.length === 0}
          sx={{
            fontWeight: 700,
            px: 3,
            py: 1.5,
            fontSize: '1rem',
            boxShadow: 3,
            '&:hover': {
              boxShadow: 6,
              transform: 'translateY(-2px)',
              transition: 'all 0.2s',
            },
          }}
        >
          AI Risk Assessment
        </Button>
        {(pregnancy.risk_category === 'high' || pregnancy.risk_category === 'critical') && (
          <Button variant="contained" color="error" startIcon={<AlertTriangle size={16} />}
            onClick={() => { setEmergencyOpen(true); setActionError(null); }}>
            Trigger Emergency
          </Button>
        )}
      </Box>

      {/* Record Vitals Dialog */}
      <Dialog open={vitalsOpen} onClose={() => setVitalsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>Record Vital Signs</DialogTitle>
        <DialogContent>
          {actionError && <Alert severity="error" sx={{ mb: 2, mt: 1, borderRadius: 2 }}>{actionError}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1.5}>
              <TextField fullWidth label="BP Systolic *" type="number" value={vitalsForm.bp_systolic}
                onChange={(e) => setVitalsForm({ ...vitalsForm, bp_systolic: e.target.value })} />
              <TextField fullWidth label="BP Diastolic *" type="number" value={vitalsForm.bp_diastolic}
                onChange={(e) => setVitalsForm({ ...vitalsForm, bp_diastolic: e.target.value })} />
            </Stack>
            <TextField fullWidth label="Heart Rate (bpm) *" type="number" value={vitalsForm.heart_rate}
              onChange={(e) => setVitalsForm({ ...vitalsForm, heart_rate: e.target.value })} />
            <TextField fullWidth label="Temperature (°C)" type="number" value={vitalsForm.temperature}
              onChange={(e) => setVitalsForm({ ...vitalsForm, temperature: e.target.value })} />
            <TextField fullWidth label="Weight (kg)" type="number" value={vitalsForm.weight}
              onChange={(e) => setVitalsForm({ ...vitalsForm, weight: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setVitalsOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleRecordVitals} disabled={actionLoading}
            sx={{ textTransform: 'none', fontWeight: 700 }}>
            {actionLoading ? <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} /> : null} Save Vitals
          </Button>
        </DialogActions>
      </Dialog>

      {/* Trigger Emergency Dialog */}
      <Dialog open={emergencyOpen} onClose={() => setEmergencyOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700} sx={{ color: '#dc2626' }}>Trigger Emergency</DialogTitle>
        <DialogContent>
          {actionError && <Alert severity="error" sx={{ mb: 2, mt: 1, borderRadius: 2 }}>{actionError}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField fullWidth select label="Event Type" value={emergencyForm.event_type}
              onChange={(e) => setEmergencyForm({ ...emergencyForm, event_type: e.target.value })}>
              {['HIGH_BP_EMERGENCY', 'PREMATURE_LABOR', 'SEVERE_BLEEDING', 'ECLAMPSIA', 'FETAL_DISTRESS', 'OTHER'].map((t) => (
                <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>
              ))}
            </TextField>
            <TextField fullWidth select label="Severity" value={emergencyForm.severity_level}
              onChange={(e) => setEmergencyForm({ ...emergencyForm, severity_level: e.target.value })}>
              {['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
            <TextField fullWidth label="Symptoms / Notes" multiline rows={2} value={emergencyForm.symptoms}
              onChange={(e) => setEmergencyForm({ ...emergencyForm, symptoms: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEmergencyOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleTriggerEmergency} disabled={actionLoading}
            sx={{ textTransform: 'none', fontWeight: 700 }}>
            {actionLoading ? <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} /> : null} Trigger Emergency
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PregnancyDetails;